import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { initiativesApi } from '../services/trackerApi';
import { peopleApi } from '../services/api';
import { PageHeader } from '../components/PageHeader';
import { NimbusTable } from '../components/NimbusTable';
import { SortableTh } from '../components/SortableTh';
import { useSortableData } from '../hooks/useSortableData';
import { usePagination } from '../hooks/usePagination';
import { Pagination } from '../components/Pagination';
import { CATEGORIES, CATEGORY_COLOR, HEALTH_COLOR, STATUS_COLOR, effectiveHealth, TIER_TONE, TIER_ICON, tierSlug, initials } from '../utils/format';
import { toneFromMap, SEVERITY_TONE } from '../constants/tone';
import { buildChildrenIndex, getRootInitiatives, getUnreachableInitiatives } from '../utils/initiativeHierarchy';
import type { Initiative } from '../types/trackerModels';

type SortField = 'name' | 'category' | 'owner' | 'status' | 'health' | 'priority' | 'percentComplete';

export const AllInitiativesPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: initiatives = [] } = useQuery<Initiative[]>({ queryKey: ['initiatives'], queryFn: initiativesApi.getAll });
  const { data: people = [] } = useQuery<{ personId: string; personName: string }[]>({ queryKey: ['people'], queryFn: peopleApi.getAll });
  const [category, setCategory] = React.useState<string>('All');
  // Business as Usual (ongoing standing work, no real end date) is a genuinely different shape
  // of thing from a time-bound project - health/priority/progress don't mean anything for it -
  // so it gets its own view rather than being mixed into the same table.
  const [view, setView] = React.useState<'projects' | 'bau'>('projects');
  const [collapsedIds, setCollapsedIds] = React.useState<Set<string>>(new Set());
  const [expandLevel, setExpandLevel] = React.useState<'portfolio' | 'program' | 'project'>('portfolio');
  // Matches the 'bau' view below: a BAU item with a real tier/parentInitiativeId now nests
  // under its parent in the 'projects' tree instead, so it isn't counted here either.
  const bauCount = initiatives.filter((i) => i.isBau && !i.parentInitiativeId).length;

  const personName = (id: string) => people.find((p) => p.personId === id)?.personName || 'Unassigned';
  // A BAU item with a real tier/parentInitiativeId is a recognised part of its parent's
  // structure - it belongs in the 'projects' tree, nested under that parent, not hidden in the
  // flat BAU list. Untiered BAU stays exactly where it always was.
  const byView = initiatives.filter((i) => (view === 'bau' ? !!i.isBau && !i.parentInitiativeId : !i.isBau || i.parentInitiativeId));
  const filtered = category === 'All' ? byView : byView.filter((i) => i.category === category);

  const toggleCollapsed = (initiativeId: string) => {
    setCollapsedIds((current) => {
      const next = new Set(current);
      if (next.has(initiativeId)) next.delete(initiativeId);
      else next.add(initiativeId);
      return next;
    });
  };

  // The Portfolio/Program/Project hierarchy only makes sense to show as a tree over the
  // *complete*, unfiltered set of Projects-view initiatives - filtering to one category would
  // silently break branches (a Program in one category with Projects in another would lose
  // them), so the tree only renders when nothing is excluding a node from its parent's branch.
  const showTree = view === 'projects' && category === 'All';

  const childrenByParentId = React.useMemo(() => buildChildrenIndex(filtered), [filtered]);
  const rootInitiatives = React.useMemo(() => getRootInitiatives(filtered), [filtered]);
  // See utils/initiativeHierarchy.ts's own doc comment for why this check exists - without it,
  // a cyclic parent reference makes an entire branch silently vanish from the page rather than
  // erroring, which is exactly what happened here before this was factored out and fixed.
  const unreachableInitiatives = React.useMemo(
    () => getUnreachableInitiatives(filtered, rootInitiatives, childrenByParentId),
    [filtered, rootInitiatives, childrenByParentId],
  );

  /** Collapses every node whose children would sit AT OR BEYOND the given tier, so
   *  "Portfolio" shows only roots, "Program" shows roots + their direct children, "Project"
   *  is fully expanded. Depth is positional (root = 0), not the node's own tier value - a
   *  standalone, tier-less initiative is still depth 0, same as a real Portfolio.
   *
   *  Recursion never stops at the target depth - it keeps walking every node's children
   *  regardless of whether that node just got collapsed. Otherwise "Portfolio" only ever
   *  collapsed the portfolios themselves and never even visited the programs underneath, so
   *  expanding a portfolio by hand dumped its programs open with their projects already
   *  showing, instead of one tier at a time. */
  const collapsedIdsForLevel = React.useCallback((level: 'portfolio' | 'program' | 'project'): Set<string> => {
    const targetDepth = level === 'portfolio' ? 0 : level === 'program' ? 1 : Infinity;
    const collapsed = new Set<string>();
    const walk = (node: Initiative, depth: number) => {
      const children = childrenByParentId.get(node.initiativeId) || [];
      if (children.length === 0) return;
      if (depth >= targetDepth) collapsed.add(node.initiativeId);
      children.forEach((child) => walk(child, depth + 1));
    };
    rootInitiatives.forEach((root) => walk(root, 0));
    return collapsed;
  }, [rootInitiatives, childrenByParentId]);

  const setLevel = (level: 'portfolio' | 'program' | 'project') => {
    setExpandLevel(level);
    setCollapsedIds(collapsedIdsForLevel(level));
  };

  // Opens with everything collapsed to Portfolio level - so the page reads as a portfolio
  // list first, not a wall of every project at once - the moment real tree data is
  // available. Guarded to fire once: a later background refetch (e.g. after editing an
  // initiative elsewhere) must never reset whatever the user has since expanded by hand.
  const collapseInitialised = React.useRef(false);
  React.useEffect(() => {
    if (collapseInitialised.current || rootInitiatives.length === 0) return;
    collapseInitialised.current = true;
    setCollapsedIds(collapsedIdsForLevel('portfolio'));
  }, [rootInitiatives, collapsedIdsForLevel]);

  const { sorted, field: sortField, dir: sortDir, toggle: handleSort } = useSortableData<Initiative, SortField>(
    filtered,
    (i, f) => (f === 'owner' ? personName(i.ownerId) : f === 'percentComplete' ? i.percentComplete : i[f]),
    'name',
  );
  const { pageItems, page, setPage, totalPages, pageSize, totalItems } = usePagination(sorted, 25);

  const ownerCell = (i: Initiative) => (
    <span className="d-flex align-items-center gap-2">
      <span className="avatar-init xs" style={{ background: 'color-mix(in srgb, var(--bs-paper-bg) 84%, var(--bs-secondary))', color: 'var(--bs-secondary)' }}>
        {initials(personName(i.ownerId))}
      </span>
      {personName(i.ownerId)}
    </span>
  );

  const tierChip = (i: Initiative) => i.tier
    ? <span className={`np-chip np-tone-${TIER_TONE[i.tier] || 'neutral'}`}>{i.tier}</span>
    : <span className="np-sub">—</span>;

  /** Small icon badge reinforcing tier at a glance - Portfolio/Program/Project each get a
   *  distinct icon, not just a distinct colour, so the row reads correctly even for anyone
   *  colour-blind to the purple/blue/grey the Tier chip alone relies on. */
  const tierBadge = (i: Initiative) => {
    const slug = tierSlug(i.tier);
    return (
      <div className={`np-tier-badge np-tier-badge-${slug}`} title={i.tier || 'No tier'}>
        <i className={i.tier ? TIER_ICON[i.tier] : 'ri-checkbox-blank-circle-line'}></i>
      </div>
    );
  };

  const projectCells = (i: Initiative) => (
    <>
      <td><span className={`np-chip np-tone-${toneFromMap(STATUS_COLOR, i.status, 'neutral')}`}>{i.status}</span></td>
      <td><span className={`np-chip np-tone-${toneFromMap(HEALTH_COLOR, effectiveHealth(i), 'neutral')}`}>{effectiveHealth(i)}</span></td>
      <td><span className={`np-chip np-tone-${toneFromMap(SEVERITY_TONE, i.priority, 'neutral')}`}>{i.priority}</span></td>
      <td>
        <div className="d-flex align-items-center gap-2">
          <div className="np-progress" style={{ width: '60px' }}><div className="np-fill" style={{ width: `${i.percentComplete}%` }}></div></div>
          <span className="np-tabular" style={{ fontSize: '11.5px' }}>{i.percentComplete}%</span>
        </div>
      </td>
    </>
  );

  /** `ancestorLines[k]` is true when the ancestor at depth k has a later sibling still to
   *  come - i.e. this row's tree guide column k needs a continuing vertical stroke, not
   *  empty space, because something further down the list still branches off that ancestor. */
  const renderTreeRows = (i: Initiative, depth: number, ancestorLines: boolean[] = []): React.ReactNode => {
    const children = childrenByParentId.get(i.initiativeId) || [];
    const hasChildren = children.length > 0;
    const isCollapsed = collapsedIds.has(i.initiativeId);
    const slug = tierSlug(i.tier);
    // Whether THIS row has a later sibling - the last entry ancestorLines carries in for it -
    // decides whether its own corner mark is a tee (continues) or an elbow (stops here).
    const isLastChild = ancestorLines.length > 0 && !ancestorLines[ancestorLines.length - 1];

    return (
      <React.Fragment key={i.initiativeId}>
        <tr
          className={slug === 'portfolio' || slug === 'program' ? `np-tier-row-${slug}` : undefined}
          style={{ cursor: 'pointer' }}
          onClick={() => navigate(`/tracker/initiatives/${i.initiativeId}`)}
        >
          <td className={`np-tier-cell-${slug}`}>
            <div className="d-flex align-items-center">
              {ancestorLines.slice(0, -1).map((hasMore, k) => (
                <span key={k} className={`np-tree-guide${hasMore ? ' np-tree-guide-line' : ''}`}></span>
              ))}
              {depth > 0 && (
                <span className={`np-tree-guide np-tree-guide-corner${isLastChild ? ' np-tree-guide-corner-last' : ''}`}></span>
              )}
              {hasChildren ? (
                <button
                  type="button"
                  className="np-icon-btn me-1"
                  onClick={(e) => { e.stopPropagation(); toggleCollapsed(i.initiativeId); }}
                  title={isCollapsed ? 'Expand' : 'Collapse'}
                >
                  <i className={isCollapsed ? 'ri-arrow-right-s-line' : 'ri-arrow-down-s-line'}></i>
                </button>
              ) : (
                <span style={{ display: 'inline-block', width: '1.875rem' }}></span>
              )}
              {tierBadge(i)}
              <Link to={`/tracker/initiatives/${i.initiativeId}`} className="np-row-title fw-semibold ms-2" onClick={(e) => e.stopPropagation()}>{i.name}</Link>
              {hasChildren && (
                // A long name wraps to 2 lines, which stretches this whole flex row taller;
                // align-items:center on the row would then centre this short chip against
                // the WHOLE wrapped block, leaving it floating between the two lines rather
                // than sitting level with the name - alignSelf pins it to the first line
                // instead, matching where the eye actually reads it next to the title.
                <span className="np-chip np-tone-neutral np-tabular ms-2" style={{ fontSize: '0.7rem', alignSelf: 'flex-start', marginTop: '0.1rem' }}>
                  {children.length}
                </span>
              )}
            </div>
          </td>
          <td>{tierChip(i)}</td>
          <td><span className={`np-chip np-tone-${toneFromMap(CATEGORY_COLOR, i.category, 'neutral')}`}>{i.category}</span></td>
          <td>{ownerCell(i)}</td>
          {projectCells(i)}
        </tr>
        {hasChildren && !isCollapsed && children.map((child, idx) =>
          renderTreeRows(child, depth + 1, [...ancestorLines, idx !== children.length - 1])
        )}
      </React.Fragment>
    );
  };

  // Deliberately flat, non-recursive - unlike renderTreeRows above, this never looks up its own
  // childrenByParentId entry. A node only ends up here because its parent chain is broken (a
  // cycle), so recursing into its "children" risks re-entering the very cycle that stranded it
  // in the first place (A's child is B, B's child is A - infinite recursion, not just a missed
  // row). Showing it flat, once, with a warning is correct; showing it nested again is not safe.
  const renderUnreachableRow = (i: Initiative): React.ReactNode => (
    <tr
      key={i.initiativeId}
      style={{ cursor: 'pointer', background: 'color-mix(in srgb, var(--bs-warning) 6%, var(--np-surface))' }}
      onClick={() => navigate(`/tracker/initiatives/${i.initiativeId}`)}
    >
      <td>
        <div className="d-flex align-items-center gap-2">
          <i className="ri-error-warning-line text-warning" title="Circular or broken parent reference - shown here rather than under its parent"></i>
          <Link to={`/tracker/initiatives/${i.initiativeId}`} className="np-row-title fw-semibold" onClick={(e) => e.stopPropagation()}>{i.name}</Link>
          <span className="np-chip np-tone-warning" style={{ fontSize: '0.7rem' }}>Broken parent link</span>
        </div>
      </td>
      <td>{tierChip(i)}</td>
      <td><span className={`np-chip np-tone-${toneFromMap(CATEGORY_COLOR, i.category, 'neutral')}`}>{i.category}</span></td>
      <td>{ownerCell(i)}</td>
      {projectCells(i)}
    </tr>
  );

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-flashlight-line"
        title="All Initiatives"
        subtitle="Every tracked initiative across the portfolio"
        breadcrumbs={[{ label: 'Initiative Tracker', to: '/tracker' }, { label: 'All Initiatives' }]}
      />

      <div className="np-card">
        <div className="np-card-head" style={{ flexWrap: 'wrap', rowGap: '0.5rem' }}>
          <h2>All initiatives</h2>
          <div className="d-flex gap-2 flex-wrap" style={{ marginLeft: 'auto' }}>
            {showTree && (
              <div className="np-pill-toggle-group" role="group" aria-label="Expand tree to level">
                <button
                  className={`np-pill-toggle np-tone-primary${expandLevel === 'portfolio' ? ' active' : ''}`}
                  title="Show Portfolios only"
                  onClick={() => setLevel('portfolio')}
                >
                  Portfolio
                </button>
                <button
                  className={`np-pill-toggle np-tone-primary${expandLevel === 'program' ? ' active' : ''}`}
                  title="Show Portfolios and their Programs"
                  onClick={() => setLevel('program')}
                >
                  Program
                </button>
                <button
                  className={`np-pill-toggle np-tone-primary${expandLevel === 'project' ? ' active' : ''}`}
                  title="Expand everything, including Projects"
                  onClick={() => setLevel('project')}
                >
                  Project
                </button>
              </div>
            )}
            <div className="np-pill-toggle-group" role="group">
              <button className={`np-pill-toggle np-tone-primary${view === 'projects' ? ' active' : ''}`} onClick={() => setView('projects')}>Initiatives</button>
              <button className={`np-pill-toggle np-tone-primary${view === 'bau' ? ' active' : ''}`} onClick={() => setView('bau')}>
                Standalone Work{bauCount > 0 ? ` (${bauCount})` : ''}
              </button>
            </div>
            <div className="np-pill-toggle-group" role="group">
              <button className={`np-pill-toggle np-tone-primary${category === 'All' ? ' active' : ''}`} onClick={() => setCategory('All')}>All</button>
              {CATEGORIES.map((c) => (
                <button key={c} className={`np-pill-toggle np-tone-primary${category === c ? ' active' : ''}`} onClick={() => setCategory(c)}>{c}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="np-card-body p-0">
        {view === 'projects' && category !== 'All' && (
          <div className="px-3 pt-3">
            <span className="np-hint mb-0">Filtering by category shows a flat list — the Portfolio/Program/Project tree needs the full set to stay intact.</span>
          </div>
        )}

        {filtered.length === 0 ? (
          <div className="text-center py-5">
            <i className="ri-flashlight-line d-block mb-2" style={{ fontSize: '1.75rem', color: 'var(--bs-secondary-color)' }}></i>
            <p className="np-sub mb-0">
              {view === 'bau' ? 'No Standalone Work initiatives yet.' : 'No initiatives in this category yet.'}
            </p>
          </div>
        ) : showTree ? (
          <NimbusTable thead={
            <tr>
              <th>Initiative</th>
              <th style={{ width: '110px' }}>Tier</th>
              <th style={{ width: '180px' }}>Category</th>
              <th style={{ width: '180px' }}>Owner</th>
              <th style={{ width: '140px' }}>Status</th>
              <th style={{ width: '130px' }}>Health</th>
              <th style={{ width: '120px' }}>Priority</th>
              <th style={{ width: '110px' }}>Progress</th>
            </tr>
          }>
            {rootInitiatives.map((i) => renderTreeRows(i, 0))}
            {unreachableInitiatives.map((i) => renderUnreachableRow(i))}
          </NimbusTable>
        ) : (
          <>
            <NimbusTable thead={view === 'bau' ? (
                  <tr>
                    <SortableTh field="name" active={sortField === 'name'} dir={sortDir} onSort={handleSort}>Initiative</SortableTh>
                    <th style={{ width: '110px' }}>Tier</th>
                    <SortableTh field="category" active={sortField === 'category'} dir={sortDir} onSort={handleSort} width="180px">Category</SortableTh>
                    <SortableTh field="owner" active={sortField === 'owner'} dir={sortDir} onSort={handleSort} width="180px">Owner</SortableTh>
                  </tr>
                ) : (
                  <tr>
                    <SortableTh field="name" active={sortField === 'name'} dir={sortDir} onSort={handleSort}>Initiative</SortableTh>
                    <th style={{ width: '110px' }}>Tier</th>
                    <SortableTh field="category" active={sortField === 'category'} dir={sortDir} onSort={handleSort} width="180px">Category</SortableTh>
                    <SortableTh field="owner" active={sortField === 'owner'} dir={sortDir} onSort={handleSort} width="180px">Owner</SortableTh>
                    <SortableTh field="status" active={sortField === 'status'} dir={sortDir} onSort={handleSort} width="140px">Status</SortableTh>
                    <SortableTh field="health" active={sortField === 'health'} dir={sortDir} onSort={handleSort} width="130px">Health</SortableTh>
                    <SortableTh field="priority" active={sortField === 'priority'} dir={sortDir} onSort={handleSort} width="120px">Priority</SortableTh>
                    <SortableTh field="percentComplete" active={sortField === 'percentComplete'} dir={sortDir} onSort={handleSort} width="110px">Progress</SortableTh>
                  </tr>
                )}>
              {pageItems.map((i) => {
                const slug = tierSlug(i.tier);
                return (
                  <tr
                    key={i.initiativeId}
                    className={slug === 'portfolio' || slug === 'program' ? `np-tier-row-${slug}` : undefined}
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/tracker/initiatives/${i.initiativeId}`)}
                  >
                    <td className={`np-tier-cell-${slug}`}>
                      <div className="d-flex align-items-center">
                        {tierBadge(i)}
                        <Link to={`/tracker/initiatives/${i.initiativeId}`} className="np-row-title fw-semibold ms-2" onClick={(e) => e.stopPropagation()}>{i.name}</Link>
                      </div>
                    </td>
                    <td>{tierChip(i)}</td>
                    <td><span className={`np-chip np-tone-${toneFromMap(CATEGORY_COLOR, i.category, 'neutral')}`}>{i.category}</span></td>
                    <td>{ownerCell(i)}</td>
                    {view === 'projects' && projectCells(i)}
                  </tr>
                );
              })}
            </NimbusTable>
            {pageItems.length > 0 && (
              <Pagination page={page} totalPages={totalPages} pageSize={pageSize} totalItems={totalItems} onPageChange={setPage} />
            )}
          </>
        )}
        </div>
      </div>
    </div>
  );
};
